import { UIStatusGroup, UIStatusGroupItem } from '../types';
import { SalesOrderStatusGroupDto, SalesOrderStatusHistoryDto } from 'src/models/salesorder';
import { AttachmentDto, CustomMetadataDto } from '../../../models/attachment';
import { Dispatch, SetStateAction } from 'react';

/**
 * Groups the sales order status data by date and status group ID.
 *
 * @param {SalesOrderStatusHistoryDto[]} statusData - The array of sales order status data.
 * @return {Record<string, Record<string, SalesOrderStatusHistoryDto[]>>} - The grouped sales order status data.
 */
export const groupStatusByDateThenStatusGroupId = (statusData: SalesOrderStatusHistoryDto[]) => {
  // Create an empty object to store the grouped statuses
  const groupedStatuses: Record<string, Record<string, SalesOrderStatusHistoryDto[]>> = {};

  // Loop through each status data
  for (const status of statusData) {
    // Extract the sales order status group ID and status date from the status object
    const { salesOrderStatus: { salesOrderStatusGroupId = '' } = {}, statusDate = new Date() } = status;

    // Get the date only without the time
    const dateOnly = new Date(statusDate).toLocaleDateString();

    // If the date is not in the groupedStatuses object, create a new entry
    if (!groupedStatuses[dateOnly]) {
      groupedStatuses[dateOnly] = {};
    }

    // If the sales order status group ID is not in the date entry, create a new entry
    if (!groupedStatuses[dateOnly][salesOrderStatusGroupId]) {
      groupedStatuses[dateOnly][salesOrderStatusGroupId] = [];
    }

    // Add the status to the corresponding group
    groupedStatuses[dateOnly][salesOrderStatusGroupId].push(status);
  }

  // Return the grouped statuses
  return groupedStatuses;
};

/**
 * Converts an array of SalesOrderStatusGroupDto objects to an array of UIStatusGroup objects.
 * @param statusGroups - An array of SalesOrderStatusGroupDto objects.
 * @returns An array of UIStatusGroup objects.
 */
export const getUiGroupsFromStatusGroups = (statusGroups: SalesOrderStatusGroupDto[]) => {
  return statusGroups
    .sort((a: SalesOrderStatusGroupDto, b: SalesOrderStatusGroupDto) => b.displayOrder! - a.displayOrder!)
    .map(
      // Convert SalesOrderStatusGroupDto to UIStatusGroup
      (group: SalesOrderStatusGroupDto): UIStatusGroup => ({
        id: group?.id ?? '',
        date: '',
        statusTypeId: group?.id ?? '',
        description: group?.description ?? '',
        code: group?.code ?? '',
        icon: group?.icon ?? '',
        iconColour: group?.iconColour ?? '',
        statusColour: group?.statusColour ?? '',
        statusDetails: {},
        advisories: [],
        warnings: [],
        statusDocuments: [],
        items: [],
        alwaysDisplay: group?.alwaysDisplay ?? false,
        displayOrder: group?.displayOrder ?? 0,
        isActive: group?.isActive ?? false
      })
    );
};

export const getUiGroupsFromStatusObject = (statusGroups: UIStatusGroup[]) => {
  return statusGroups
    .sort((a: UIStatusGroup, b: UIStatusGroup) => b.displayOrder! - a.displayOrder!)
    .map(
      // Convert SalesOrderStatusGroupDto to UIStatusGroup
      (group: UIStatusGroup): UIStatusGroup => ({
        id: group?.id ?? '',
        date: '',
        statusTypeId: group?.id ?? '',
        description: group?.description ?? '',
        code: group?.code ?? '',
        icon: group?.icon ?? '',
        iconColour: group?.iconColour ?? '',
        statusColour: group?.statusColour ?? '',
        statusDetails: {},
        advisories: [],
        warnings: [],
        statusDocuments: [],
        items: [],
        alwaysDisplay: group?.alwaysDisplay ?? false,
        displayOrder: group?.displayOrder ?? 0,
        isActive: group?.isActive ?? false,
        showLine: group?.showLine ?? false
      })
    );
};

/**
 * Combines the UI status groups and grouped statuses into a single object.
 *
 * @param {UIStatusGroup[]} uiGroups - An array of UI status groups.
 * @param {Record<string, Record<string, SalesOrderStatusHistoryDto[]>>} groupedStatuses - A nested object that represents grouped statuses.
 * @param {AttachmentDto[]} attachments - An array of attachments.
 * @return {Record<string, UIStatusGroup[]>} - A combined object of UI status groups and grouped statuses.
 */
export const combineGroupsAndStatuses = (
  uiGroups: UIStatusGroup[],
  groupedStatuses: Record<string, Record<string, SalesOrderStatusHistoryDto[]>>,
  attachments: AttachmentDto[],
  acceptedAttachmentTypeIds: string[],
  deliveryCountryCode: string
): Record<string, UIStatusGroup[]> => {
  // Initialize an empty object to store the combined UI status groups and grouped statuses
  const res: any = {};

  // Iterate over the entries of the groupedStatuses object
  Object.entries(groupedStatuses).forEach(([date, groups]) => {
    // Map over the entries of the groups object
    const groupArray = Object.entries(groups).map(([statusId, statuses]) => {
      // Find the UI status group that matches the current statusId
      const uiGroup = uiGroups.find(uiGroup => uiGroup.statusTypeId === statusId);

      // Map over the statuses array to transform each status into a UIStatusGroupItem object, sort items by date - newest first
      const items = statuses
        .map((status: SalesOrderStatusHistoryDto): UIStatusGroupItem => {
          status = handleReversedLatLong(status, deliveryCountryCode);

          return {
            salesOrderId: status?.salesOrderId ?? '',
            salesOrderItemId: status?.salesOrderItemId ?? '',
            statusDate: status?.statusDate ?? '',
            itemId: status?.id ?? '',
            podName: status?.podName ?? '',
            notes: status?.notes ?? '',
            latitude: status?.latitude,
            longitude: status?.longitude,
            ...status.salesOrderStatus
          };
        })
        .sort((a: UIStatusGroupItem, b: UIStatusGroupItem) => {
          if (!a?.statusDate) {
            a = { statusDate: new Date().toISOString() };
          }

          if (!b?.statusDate) {
            b = { statusDate: new Date().toISOString() };
          }

          return (new Date(b.statusDate as string) as any) - (new Date(a.statusDate as string) as any);
        });

      // Add any status notes to the status groups 'statusDetails' object
      const statusDetails = items.reduce((acc: Record<string, any>, curr, index) => {
        return { ...acc, ...(curr?.notes && { [`Item ${index + 1}`]: curr.notes }) };
      }, {});

      /**
       * Find any attachments which have metadata referencing any of the items and add
       * them to the group documents.
       */
      const statusDocuments = attachments.filter((attachment: AttachmentDto) => {
        const { metadata } = attachment;

        const salesOrderStatusMeta = metadata?.find(
          (meta: CustomMetadataDto) => meta.key === 'SalesOrderStatus' || meta.key === 'SalesOrderStatusId'
        );

        if (salesOrderStatusMeta) {
          const statusId = salesOrderStatusMeta.value;

          if (items.some(item => item.id === statusId)) {
            return true;
          }

          return false;
        } else {
          if (
            uiGroup?.description === 'Delivered' &&
            acceptedAttachmentTypeIds.includes(attachment?.attachmentTypeId ?? '')
          ) {
            return true;
          }
        }

        return false;
      });

      // Return an object that combines the UI status group, date, and transformed statuses
      return {
        ...uiGroup,
        date,
        items,
        statusDetails,
        statusDocuments
      };
    });

    // Assign the groupArray to the corresponding date in the res object
    res[date] = groupArray;
  });

  // Return the combined UI status groups and grouped statuses
  return res;
};

/**
 * Filters and sorts the UI status groups based on specific conditions.
 *
 * @param {Record<string, UIStatusGroup[]>} combinedGroupsAndStatuses - The combined groups and statuses.
 * @param {UIStatusGroup[]} uiGroups - The UI groups.
 * @return {UIStatusGroup[]} The filtered and sorted UI status groups.
 */
export const filterAndSortGroups = (
  combinedGroupsAndStatuses: Record<string, UIStatusGroup[]>,
  uiGroups: UIStatusGroup[]
): UIStatusGroup[] => {
  // Flatten the values of combinedGroupsAndStatuses and concatenate with uiGroups
  const flat = Object.values(combinedGroupsAndStatuses).flat();
  const merged = flat.concat(uiGroups);

  /**
   * Sort the merged array based on the displayOrder property in descending order,
   * if comparing dates when displayOrders are equal, sort by date descending.
   */
  const sorted = merged.sort((a: any, b: any) => {
    if (a.displayOrder === b.displayOrder) {
      // When displayOrders are equal, sort by date descending
      return convertToDate(b.date).getTime() - convertToDate(a.date).getTime();
    }
    // Primary sort by displayOrder descending

    return b.displayOrder - a.displayOrder;
  });

  // Filter the sorted array based on specific conditions
  const filtered = sorted.filter(value => {
    const duplicates = sorted.filter(v => v.statusTypeId === value.statusTypeId);

    if (duplicates.length > 1) {
      if (!value?.items || value.items.length === 0) {
        return false;
      }

      return true;
    }

    if (!value?.items || value.items.length === 0) {
      if (value.alwaysDisplay) {
        return true;
      }

      return false;
    }

    return false;
  });

  return filtered;
};

/**
 * Determines the currently active status based on the given status array.
 *
 * @param {UIStatusGroup[]} statusArray - An array of UIStatusGroup objects
 */
export const determineCurrentlyActiveStatus = (
  statusArray: UIStatusGroup[],
  setter: Dispatch<SetStateAction<string>>
) => {
  // Find the first entry in filtered that has items and set the currently active status id to that entry's id
  const firstWithItems = statusArray.find(value => value?.items?.length > 0);

  if (firstWithItems) {
    setter(firstWithItems.id);
  }
};

/**
 * Filters out empty discrepancies from the given array of UIStatusGroup objects.
 *
 * @param {UIStatusGroup[]} groupArray - The array of UIStatusGroup objects to filter.
 * @return {UIStatusGroup[]} - The filtered array of UIStatusGroup objects.
 */
export const filterEmptyDiscrepancies = (groupArray: UIStatusGroup[]): UIStatusGroup[] => {
  const discrepIndex = groupArray.findIndex(group => group.code === 'Discrep');

  if (discrepIndex >= 0 && groupArray[discrepIndex].items.length < 1) {
    groupArray.splice(discrepIndex, 1);
  }

  return groupArray;
};

/**
 * Get color for order item status text.
 *
 * @param status OrderItemStatusType
 * @returns string
 */
export const getColorForItemStatus = (status: string): string => {
  switch (status) {
    case 'Arrived':
      return '#32CD32';
    default:
      return '#2B2D42';
  }
};

/**
 * Get Icon for status.
 *
 * @param statusName StatusType
 * @param hasAdvisories boolean
 * @param hasWarnings boolean
 * @param isComplete boolean
 * @param isActiveStats boolean
 * @returns string
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getIconForStatus = (
  icon: string,
  hasAdvisories: boolean,
  hasWarnings: boolean,
  isComplete: boolean,
  isActiveStatus: boolean,
  overrideComplete?: boolean
): string => {
  switch (icon) {
    case 'Delivered':
      return `/images/icons/order_status/Icon_Delivered${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
    case 'OutForDelivery':
      return `/images/icons/order_status/Icon_outForDelivery${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
    case 'In Progress':
      return `/images/icons/order_status/Icon_outForDelivery${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
    case 'AtHub':
      return `/images/icons/order_status/Icon_AtHub${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
    case 'Outstanding':
      return `/images/icons/order_status/Icon_AtHub${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
    case 'Planned':
      return `/images/icons/order_status/Icon_Planned${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
    case 'Acknowledged':
      return `/images/icons/order_status/Icon_Acknowledged${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
    case 'AtDelDepot':
    case 'AtDepot':
    case 'ReadyForCollection':
    case 'CRT':
    case 'DEFAULT':
      return `/images/icons/order_status/Icon_AtDepot${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
    case 'Collected':
      return `/images/icons/order_status/Icon_Collected${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
    default:
      return `/images/icons/order_status/Icon_AtDepot${
        hasAdvisories
          ? '_advisory'
          : hasWarnings
          ? '_warning'
          : isActiveStatus
          ? '_active'
          : isComplete || overrideComplete
          ? '_complete'
          : ''
      }.svg`;
  }
};

/**
 * Determine if status has advisories.
 *
 * @param status Status
 * @returns boolen
 */
export const statusHasAdvisories = (status: UIStatusGroup): boolean => {
  return status?.advisories?.length ? true : false;
};

/**
 *  Determine if status has warnings.
 *
 * @param status Status
 * @returns boolean
 */
export const statusHasWarnings = (status: UIStatusGroup): boolean => {
  return status?.warnings?.length ? true : false;
};

/**
 * Determine whether the dashed connecting line is
 * shown for a status.
 *
 * @param status Status
 * @returns boolean
 */
export const showConnectingLineForStatus = (status: UIStatusGroup): boolean => {
  const isComplete = Boolean(status.date);
  const hasAdvisories = statusHasAdvisories(status);
  const hasWarnings = statusHasWarnings(status);

  return !hasAdvisories && !hasWarnings && isComplete;
};

/**
 * Determines whether the element at the given index in the parent array should be considered complete.
 *
 * @param {UIStatusGroup[]} parentArray - The array containing the UIStatusGroup objects.
 * @param {number} index - The index of the element in the parent array.
 * @param {string} activeStatusId - The ID of the active status.
 * @return {boolean} - Returns true if the element should be considered complete, false otherwise.
 */
export const determineShouldBeComplete = (parentArray: UIStatusGroup[], index: number, activeStatusId: string) => {
  // Find the index of the active status in the parent array
  const activeIndex = parentArray.findIndex(status => status.id === activeStatusId);

  // If the active status is found in the parent array
  if (activeIndex >= 0) {
    // If the index of the current element is greater than the index of the active status
    if (index > activeIndex) {
      // The element should be considered complete
      return true;
    }
  }

  // The element should not be considered complete
  return false;
};

/**
 * Convert date from dd/mm/yyyy to Date object for sorting
 *
 * @param dateString
 * @returns Date
 */
const convertToDate = (dateString: any) => {
  if (!dateString) return new Date(0); // Handle empty date strings
  const [day, month, year] = dateString.split('/');

  return new Date(year, month - 1, day); // Month is 0-indexed in JavaScript Date
};

/**
 * Handle reversed latitude and longitude for GBR
 *
 * @param status
 * @param deliveryCountryCode
 * @returns
 */
const handleReversedLatLong = (status: UIStatusGroupItem, deliveryCountryCode: string) => {
  if (deliveryCountryCode?.toLowerCase() === 'gbr') {
    if ((status?.latitude ?? 0) < (status?.longitude ?? 0)) {
      const tempLat = status?.latitude;
      status.latitude = status?.longitude || 0;
      status.longitude = tempLat || 0;
    }
  }

  return status;
};
